import { css, html, LitElement, nothing } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { IApiMediaWithCropsModel } from '../api/ContentDeliveryApi.schemas'
import { MEDIA_BASE } from '../Settings'

@customElement('lindberg-image')
export class CustomImage extends LitElement {
	static styles = css`
    :host{
        display: block;
        width: 100%;
        position: relative;
        aspect-ratio: var(--aspect-ratio);
    }

    img {
        display: block;
        width:100%;
        height: 100%;
        object-fit: cover;
        z-index:0;
        position:relative;
        border-radius: var(--border-radius);

        object-position: var(--object-position);
		aspect-ratio: var(--aspect-ratio);
    }

    .overlay {
        display: block;
        position: absolute;
        width: 100%;
        height: 100%;
        z-index: 1;
        backdrop-filter: blur(1vw);
        border-radius: var(--border-radius);
        pointer-events: none;
        transition: opacity .2s;
		/* transition-delay: 0.5s; */
		/* background-color: rgba(255, 20, 147, 0.5); */
    }

    .overlay.preloading {
        display: none;
    }

    .overlay.loading {
        opacity: 1;
    }

    .overlay.loaded{
        opacity: 0.0;
    }`

	data: IApiMediaWithCropsModel

	@state()
	isLoading: boolean = true

	@state()
	loading: 'preloading' | 'loading' | 'loaded' | 'failed' = 'preloading'

	get targetWidth() {
		return Math.max(200, Math.min(Math.ceil(this.componentWidth * this.devicePixelRatio / 100) * 100, this.data?.width ?? 200))
	}

	@state()
	get imageUrl() {
		return `${MEDIA_BASE}${this.data.url}?width=${this.loading == "preloading" ? 200 : this.targetWidth}&format=webp`
	}

	@state()
	componentWidth: number

	@state()
	devicePixelRatio: number = window.devicePixelRatio

	@state()
	visible = false

	private resizeObserver?: ResizeObserver

	connectedCallback() {
		super.connectedCallback()

		// Hidden (display: none) images report zero width; keep the last real width
		// and only start loading once the image is shown, like lindberg-video
		this.resizeObserver = new ResizeObserver(([entry]) => {
			const width = Math.round(entry.contentRect.width)
			if (width == 0) return
			this.componentWidth = width
			if (!this.visible) this.show()
		})
		this.resizeObserver.observe(this)

		window.addEventListener('resize', this.updateDevicePixelRatio)
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		this.resizeObserver?.disconnect()
		window.removeEventListener('resize', this.updateDevicePixelRatio)
	}

	private updateDevicePixelRatio = () => this.devicePixelRatio = window.devicePixelRatio

	private show() {
		this.visible = true
		this.loading = 'loading'
		// The 200px preload would be the final image, so load it directly
		if (this.targetWidth <= 200) return
		var image = new Image()
		image.src = this.imageUrl
		this.loading = image.complete ? 'loaded' : 'preloading'
	}


	imageLoad(e: Event) {
		if (this.loading == 'preloading') {
			this.loading = 'loading'
			// requestAnimationFrame(() => this.imageUrl = this.getImageUrl)
		} else if (this.loading == 'loading') {
			this.loading = 'loaded'
		}
	}

	render() {
		const aspectRatio = `${this.data.width!} / ${this.data.height!}`
		const objectPosition = `${this.data.focalPoint?.left * 100}% ${this.data.focalPoint?.top * 100}%`
		const lang = document.documentElement.lang
		const alt = this.data.properties.description?.[lang]
		const color = this.data.properties.color

		return html`
        <style>
            :host {
                --aspect-ratio: ${aspectRatio};
                --object-position: ${objectPosition};
            }
        </style>
        <div class="overlay ${this.loading}"></div>
        ${this.visible ? html`<img @load=${this.imageLoad} src="${this.imageUrl}" style=${color ? 'background-color: ' + color : nothing} alt="${alt}" />` : nothing}
        `
	}
}
